"use client";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import React from "react";
import { ButtonStyleType, SpacingType, sectionVariants } from "@/types";
import Link from "next/link";
import Image from '@/components/ui/Image';
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";

interface Hero9Props {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  contents?: {
    badge?: string;
    title?: string;
    content?: string;
    microCopy?: string; 
    image?: string;
    imageAltText?: string;
    hasButton?: boolean;
    buttonText?: string;
    buttonLink?: string;
    buttonStyle?: ButtonStyleType;
    secondaryButton?: boolean;
    secondaryButtonText?: string;
    secondaryButtonLink?: string;
    secondaryButtonStyle?: ButtonStyleType;
  };
  attributes?: React.HTMLProps<HTMLElement>;
}

const Hero9 = ({
  darkMode = false,
  spacing,
  variant = 'A',
  contents,
  attributes,
}: Hero9Props) => {

  return (
    <SectionWrapper
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={true}
      componentName="hero9"
      attributes={attributes}
      usedInPageNav={false}
      pageNavTitle="Hero 9"
    > 
      <div className="container"> 
        <div className="flex flex-col lg:flex-row gap-20 lg:gap-24">
          <div className="w-full lg:w-12/24 flex items-center">
            <div className="flex flex-col gap-6 text-center lg:text-left">
              {contents?.badge && (
                <Badge variant="outline" bold className="mx-auto lg:mx-0">
                  {contents?.badge}
                </Badge>
              )}
              {contents?.title && (
                <h1>
                  {parse(contents.title)}
                </h1>
              )}
              {contents?.content && (
                <div className="mt-4 paragraph-lg">
                  {parse(contents?.content)}
                </div>
              )}
              {contents?.hasButton && (
                <div className="mt-2">
                  <div className="flex flex-col items-center gap-4 lg:flex-row">
                    <Button
                      asChild
                      size='lg'
                      className="w-full sm:w-auto"
                      variant={contents?.buttonStyle || "primary"}
                    >
                      <Link href={contents?.buttonLink ?? ""}>{contents?.buttonText}</Link>
                    </Button>
                    {contents?.secondaryButton && (
                      <Button
                        className="w-full sm:w-auto"
                        variant={contents?.secondaryButtonStyle || "outline"}
                        asChild
                        size='lg'
                      >
                        <Link href={contents?.secondaryButtonLink ?? ""}> 
                          {contents?.secondaryButtonText}
                        </Link>
                      </Button>
                    )}
                  </div>
                  {contents?.microCopy && (
                    <p className="mt-2 paragraph-sm text-muted-foreground">
                      {contents?.microCopy}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="w-full lg:w-12/24 flex items-center justify-center rounded-xl sm:rounded-2xl xl:rounded-3xl aspect-[650/650]">
            {contents?.image && (
              <Image
                src={contents?.image}
                alt={contents?.imageAltText || ''}
                title={contents?.imageAltText || ''}
                width={750}
                height={750}
                className="object-contain w-full h-full"
                priority
                fetchPriority="high"
              />
            )}
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

export { Hero9 };
